import { forwardRef } from 'react'
import {
  ACCENT,
  INK,
  MUTE,
  RULE,
  WHITE,
  FONT_STACK,
  DISPLAY_FONT,
  SERIF_FONT,
  documentStyle,
  pillBadgeStyle,
  formatUGX,
  formatDate,
} from './documentTokens'

/**
 * ReceiptDocument — the single source of truth for the official payment
 * receipt. Both the preview modal (ReceiptModal) and the standalone full-page
 * view (student/Receipt) render THIS component, wrapping it with their own UI
 * chrome (toolbars / action bars). PDF and PNG exports capture this exact DOM
 * node via html2canvas, so the downloaded file is always a pixel-perfect match
 * of the on-screen preview.
 *
 * Design: minimalist corporate look — one terracotta accent, hairline
 * dividers, quiet labels, outlined pill badge. Landscape two-column body
 * (reference fields left, financial summary right). Fixed 980px width
 * matching the reference mockup (receipt-landscape.html).
 *
 * NOTE: This is a fixed-width document (980px). It does NOT use isMobile —
 * the receipt always renders at full landscape width. The parent container
 * should allow horizontal scrolling if the viewport is narrower than 980px.
 */

const ReceiptDocument = forwardRef(function ReceiptDocument(
  { receipt },
  ref,
) {
  if (!receipt) return null

  const fin = receipt.financials || {}
  const remainingBalance =
    fin.remaining_balance != null
      ? fin.remaining_balance
      : fin.outstanding_balance != null
        ? fin.outstanding_balance
        : 0
  const isPaid =
    receipt.invoice_status === 'paid' || parseFloat(remainingBalance || 0) <= 0

  // Label / value row with hairline divider (matches reference .row).
  // Both columns share this single function so spacing is always identical.
  // Long values wrap gracefully within their flex item instead of
  // compressing against the label.
  const row = (label, value, opts = {}) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '16px',
        padding: '10px 0',
        borderBottom: opts.noDivider ? 'none' : `1px solid ${RULE}`,
        ...(opts.borderTop ? { borderTop: `1px solid ${RULE}` } : {}),
      }}
    >
      <span
        style={{
          fontSize: '12px',
          color: MUTE,
          flexShrink: 0,
          maxWidth: '40%',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: opts.accent ? '14.5px' : opts.bold ? '14px' : '13.5px',
          color: opts.color || INK,
          fontWeight: opts.bold || opts.accent ? '600' : '400',
          ...(opts.accent ? { fontWeight: '700' } : {}),
          textAlign: 'right',
          wordBreak: 'break-word',
          flex: '1 1 0',
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  )

  return (
    <div
      ref={ref}
      className="receipt-document bg-white text-gray-900 mx-auto shadow-lg"
      style={{
        ...documentStyle(false, '980px'),
        width: '980px',
        minWidth: '980px',
        padding: '44px 56px 36px',
      }}
    >
      {/* ── Masthead — logo left, contact + status right ─────────────────── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingBottom: '20px',
        }}
      >
        {/* Logo + academy name */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
          }}
        >
          <img
            src="/Logos/SMA LOGO  VERSION 2-04.png"
            alt="Synergy Multimedia Academy"
            style={{
              height: '56px',
              width: 'auto',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <div style={{ lineHeight: 1.3, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: DISPLAY_FONT,
                fontWeight: '700',
                fontSize: '15px',
                color: INK,
                letterSpacing: '0.01em',
              }}
            >
              Synergy Multimedia Academy
            </p>
            <p
              style={{
                margin: '2px 0 0',
                fontSize: '10.5px',
                color: MUTE,
              }}
            >
              Kampala, Uganda
            </p>
          </div>
        </div>

        {/* Contact right + status badge */}
        <div
          style={{
            textAlign: 'right',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '8px',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '10.5px',
              color: MUTE,
              lineHeight: 1.6,
            }}
          >
            +256 706 739 311
            <br />
            synergymultimediaacademy@gmail.com
          </p>
          <span style={pillBadgeStyle(false, ACCENT)}>
            {isPaid ? 'Fully paid' : 'Partial payment'}
          </span>
        </div>
      </div>

      {/* ── Title row — ink border-top, serif title, ref line ────────────── */}
      <div
        style={{
          borderTop: `1px solid ${INK}`,
          paddingTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontFamily: SERIF_FONT,
              fontSize: '24px',
              fontWeight: '400',
              color: INK,
              lineHeight: 1.2,
            }}
          >
            Official payment receipt
          </p>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '11px',
              color: MUTE,
            }}
          >
            Receipt #{receipt.receipt_number || '—'}
            {receipt.payment_date ? ` · ${formatDate(receipt.payment_date, true)}` : ''}
          </p>
        </div>
      </div>

      {/* ── Body — two-column layout (landscape) ─────────────────────────── */}
      {/* Using flex, not grid — html2canvas-pro doesn't support CSS Grid,   */}
      {/* so grid would render as single-column in the exported PDF/PNG.     */}
      {/* Always two columns — the receipt is a fixed 980px document.        */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '56px',
          marginTop: '28px',
        }}
      >
        {/* Left column — Received from + reference fields */}
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          {/* Received from block */}
          <div style={{ marginBottom: '20px' }}>
            <p
              style={{
                margin: '0 0 4px',
                fontSize: '11px',
                color: MUTE,
              }}
            >
              Received from
            </p>
            <p
              style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '600',
                color: INK,
                lineHeight: 1.3,
              }}
            >
              {receipt.student?.full_name || receipt.student_name || '—'}
            </p>
            <p
              style={{
                margin: '2px 0 0',
                fontSize: '11.5px',
                color: MUTE,
                lineHeight: 1.4,
              }}
            >
              Student ID: {receipt.student?.student_number || receipt.student_number || '—'}
              {(receipt.student?.email || receipt.student_email)
                ? ` · ${receipt.student?.email || receipt.student_email}`
                : ''}
            </p>
          </div>

          {/* Reference field rows */}
          {row('Invoice number', receipt.invoice_number || '—', {
            bold: true,
            borderTop: true,
          })}
          {row('Description', receipt.payment_description || receipt.course_title || '—')}
          {receipt.course &&
            row('Course', `${receipt.course.course_code || ''} ${receipt.course.title || ''}`)}
          {row(
            'Payment method',
            (receipt.payment_method || '—').replace(/_/g, ' '),
          )}
          {receipt.transaction_reference &&
            row('Transaction reference', receipt.transaction_reference, { bold: true })}
        </div>

        {/* Right column — Financial summary */}
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <p
            style={{
              margin: '18px 0 4px',
              fontSize: '12px',
              fontWeight: '600',
              color: INK,
            }}
          >
            Financial summary
          </p>

          {row(
            'Invoice total',
            formatUGX(fin.invoice_total != null ? fin.invoice_total : receipt.amount),
            { borderTop: true },
          )}
          {row(
            'Amount previously paid',
            formatUGX(fin.amount_previously_paid != null ? fin.amount_previously_paid : 0),
            { color: MUTE },
          )}

          {/* Current Payment — accent highlight */}
          {row(
            'Current payment',
            formatUGX(fin.current_payment != null ? fin.current_payment : receipt.amount),
            { accent: true, color: ACCENT },
          )}

          {row(
            'Total paid after this payment',
            formatUGX(
              fin.total_paid_after_payment != null
                ? fin.total_paid_after_payment
                : fin.total_paid_after != null
                  ? fin.total_paid_after
                  : receipt.amount,
            ),
            { bold: true },
          )}

          {/* Remaining Balance — second accent use */}
          {row(
            'Remaining balance',
            formatUGX(remainingBalance),
            { accent: true, color: ACCENT, noDivider: true },
          )}
        </div>
      </div>

      {/* ── Footer — hairline top, space-between, muted ──────────────────── */}
      <div
        style={{
          marginTop: '28px',
          paddingTop: '14px',
          borderTop: `1px solid ${RULE}`,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          fontSize: '10px',
          color: MUTE,
        }}
      >
        <span>
          Processed by{' '}
          <span style={{ color: INK, fontWeight: '600' }}>
            {receipt.processed_by || 'System Administrator'}
          </span>
        </span>
        <span>Computer-generated receipt · Synergy Multimedia Academy</span>
      </div>
    </div>
  )
})

export default ReceiptDocument
